import * as React from "react";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { ScreenshotSpec } from "./types";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  item: ScreenshotSpec | null;
};

export function ScreenshotLightbox({ open, onOpenChange, item }: Props) {
  const reduce = useReducedMotion();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onOpenChange]);

  return (
    <AnimatePresence>
        {open && item ? (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.button
              aria-label="Close"
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => onOpenChange(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Content */}
            <motion.div
              className={cn(
                "relative w-full max-w-[1200px] overflow-hidden rounded-[28px]",
                "border border-white/10 bg-black/20 backdrop-blur-xl",
                "shadow-[0_60px_200px_rgba(0,0,0,0.55)]"
              )}
              layoutId={`shot-${item.id}`}
              transition={{ type: "spring", stiffness: 240, damping: 26, mass: 0.7 }}
            >
              <div className="absolute right-3 top-3 z-10">
                <button
                  onClick={() => onOpenChange(false)}
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-full",
                    "bg-white/10 text-white/80 hover:bg-white/15 hover:text-white",
                    "transition-colors"
                  )}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="relative">
                <img
                  src={item.src}
                  alt={item.alt}
                  className="h-auto w-full select-none object-contain"
                  draggable={false}
                />

                {/* Subtle bottom gradient for "panel" feel */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/35 to-transparent"
                />
              </div>

              {!reduce && (
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute -inset-20 -z-10"
                  initial={{ opacity: 0.25 }}
                  animate={{ opacity: [0.2, 0.3, 0.2] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="absolute inset-0 rounded-[36px] bg-[radial-gradient(55%_55%_at_30%_35%,hsl(var(--primary)/0.20),transparent_60%)] blur-3xl" />
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        ) : null}
    </AnimatePresence>
  );
}
